/**
 * Equipment Tracker - Frontend Application
 * @author Omid Zaeri (zaerio)
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import {
  AppLayout, SideNavigation, TopNavigation, BreadcrumbGroup,
  Container, Header, Spinner, Alert, SpaceBetween, Select,
} from '@cloudscape-design/components';
import '@cloudscape-design/global-styles/index.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { getUsers, getUser, checkSelfRegister, selfRegister, triggerLookup } from './services/api';
import Dashboard from './pages/Dashboard';
import AssignEquipment from './pages/AssignEquipment';
import EquipmentDetail from './pages/EquipmentDetail';
import ManageUsers from './pages/ManageUsers';

function AccessDeniedScreen() {
  const { midwayToken, reAuthenticate, viewAsNonUser, nonUserAlias } = useAuth();
  const [showEnrollment, setShowEnrollment] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollResult, setEnrollResult] = useState(null);
  const [enrollError, setEnrollError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Get the alias from the JWT (or the preview alias)
  const alias = viewAsNonUser ? (nonUserAlias || '') : (() => {
    try {
      if (!midwayToken) return '';
      const payload = JSON.parse(atob(midwayToken.split('.')[1]));
      return payload.sub || '';
    } catch { return ''; }
  })();

  const handleEnroll = async () => {
    if (!alias) return;
    setVerifying(true);
    setEnrollError(null);
    setEnrollResult(null);

    // Step 1: Trigger the lookup (creates pending marker if not cached)
    await triggerLookup(midwayToken, viewAsNonUser ? alias : undefined);

    // Step 2: Wait 15 seconds with countdown for the Cloud Desktop worker to resolve it
    setCountdown(15);
    await new Promise(resolve => {
      let remaining = 15;
      const timer = setInterval(() => {
        remaining -= 1;
        setCountdown(remaining);
        if (remaining <= 0) { clearInterval(timer); resolve(); }
      }, 1000);
    });
    setVerifying(false);
    setCountdown(0);

    // Step 3: Try to enroll
    setEnrolling(true);
    try {
      const res = await selfRegister(midwayToken, alias);
      setEnrollResult(res);
      if (!viewAsNonUser) {
        setTimeout(() => reAuthenticate(), 1500);
      }
    } catch (err) {
      setEnrollError(err.message);
    } finally {
      setEnrolling(false);
    }
  };

  if (enrollResult && !enrollError) {
    return (
      <div style={{ maxWidth: '500px', margin: '80px auto', padding: '0 16px' }}>
        <Container header={<Header variant="h1">Equipment Tracker</Header>}>
          <Alert type="success">
            Welcome, {enrollResult.name}! You've been registered as {enrollResult.tier} with admin access.
            {!viewAsNonUser && ' Redirecting to your dashboard...'}
          </Alert>
        </Container>
      </div>
    );
  }

  if (showEnrollment) {
    return (
      <div style={{ maxWidth: '500px', margin: '80px auto', padding: '0 16px' }}>
        <Container header={<Header variant="h1">Manager Self-Enrollment</Header>}>
          <SpaceBetween size="l">
            <Alert type="info">
              This enrollment is available to Amazon employees with a management title.
              Your phonetool profile will be verified automatically.
            </Alert>
            <div style={{ background: '#f4f4f4', borderRadius: 8, padding: 16 }}>
              <div style={{ fontSize: '0.82rem', color: '#525252', marginBottom: 4 }}>Your Alias</div>
              <input
                type="text"
                value={alias}
                readOnly
                style={{
                  width: '100%', padding: '10px 12px', fontSize: '1rem', fontWeight: 600,
                  border: '1px solid #c6c6c6', borderRadius: 6, background: '#e0e0e0',
                  color: '#161616', boxSizing: 'border-box',
                }}
              />
            </div>
            {enrollError && <Alert type="error">{enrollError}</Alert>}
            {verifying && (
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                <Spinner size="large" />
                <p style={{ marginTop: 8, color: '#525252', fontSize: '0.9rem' }}>
                  Verification in progress... {countdown}s
                </p>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleEnroll}
                disabled={enrolling || !alias || verifying}
                style={{
                  background: (enrolling || verifying) ? '#c6c6c6' : '#0f62fe',
                  color: '#fff', border: 'none', borderRadius: 6,
                  padding: '10px 24px', fontSize: '0.95rem', fontWeight: 600,
                  cursor: (enrolling || verifying) ? 'not-allowed' : 'pointer',
                }}
              >
                {verifying ? 'Verifying...' : enrolling ? 'Enrolling...' : 'Enroll Me'}
              </button>
              <button
                onClick={() => { setShowEnrollment(false); setEnrollError(null); }}
                style={{
                  background: 'transparent', color: '#525252', border: '1px solid #c6c6c6',
                  borderRadius: 6, padding: '10px 24px', fontSize: '0.95rem', cursor: 'pointer',
                }}
              >Back</button>
            </div>
          </SpaceBetween>
        </Container>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '500px', margin: '80px auto', padding: '0 16px' }}>
      <Container header={<Header variant="h1">Equipment Tracker</Header>}>
        <SpaceBetween size="l">
          <Alert type="error">
            You do not have access to the Equipment Tracker. Please contact your manager to request access.
          </Alert>
          <button
            onClick={() => setShowEnrollment(true)}
            style={{
              background: '#198038', color: '#fff', border: 'none', borderRadius: 6,
              padding: '10px 24px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
              width: '100%',
            }}
          >
            Manager Self-Enrollment →
          </button>
        </SpaceBetween>
      </Container>
    </div>
  );
}

function NavContent() {
  const { user, isAdmin, loading, error, viewAsNonUser } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ padding: '4rem', textAlign: 'center' }}>
        <Spinner size="large" />
        <p style={{ marginTop: '1rem' }}>Authenticating with Midway...</p>
      </div>
    );
  }

  if (viewAsNonUser || error || !user) {
    return <AccessDeniedScreen />;
  }

  const navItems = [
    { type: 'link', text: 'Dashboard', href: '/' },
  ];
  if (isAdmin) {
    navItems.push(
      { type: 'link', text: 'Assign Equipment', href: '/equipment/new' },
      { type: 'link', text: 'Manage Users', href: '/users' },
    );
  }

  const breadcrumbs = [{ text: 'Equipment Tracker', href: '/' }];
  if (location.pathname.startsWith('/equipment/new')) {
    breadcrumbs.push({ text: 'Assign Equipment', href: '/equipment/new' });
  } else if (location.pathname.startsWith('/equipment/')) {
    breadcrumbs.push({ text: 'Equipment Detail', href: location.pathname });
  } else if (location.pathname === '/users') {
    breadcrumbs.push({ text: 'Manage Users', href: '/users' });
  }

  return (
    <AppLayout
      navigationWidth={280}
      navigation={
        <SideNavigation
          header={{ text: 'Equip. Tracker', href: '/' }}
          activeHref={location.pathname}
          items={navItems}
        />
      }
      breadcrumbs={<BreadcrumbGroup items={breadcrumbs} />}
      content={
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/equipment/new" element={<AssignEquipment />} />
          <Route path="/equipment/:id" element={<EquipmentDetail />} />
          <Route path="/users" element={<ManageUsers />} />
        </Routes>
      }
      toolsHide
    />
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppContent() {
  const { user, realUser, realIsAdmin, isImpersonating, viewAsNonUser, nonUserAlias, setViewAs, clearViewAs, setNonUserView, logout } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [nonUserInput, setNonUserInput] = useState('');

  useEffect(() => {
    if (realUser?.alias === 'zaerio') {
      getUsers().then(setAllUsers).catch(console.error);
    }
  }, [realUser?.alias]);

  const badgePhotoUrl = user
    ? `https://badgephotos.corp.amazon.com/?uid=${user.alias}&Region=Master&FullsizeImage=no`
    : null;

  const userOptions = [
    { value: '__non_user__', label: '🚫 Non-user (access denied view)', description: 'Preview what non-users see' },
    ...allUsers.map(u => ({
      value: u.alias,
      label: `${u.name} (${u.alias})`,
      description: u.tier,
    })),
  ];

  const handleViewAsChange = async ({ detail }) => {
    const opt = detail.selectedOption;
    setSelectedOption(opt);
    if (!opt || opt.value === realUser?.alias) {
      clearViewAs();
      setSelectedOption(null);
      return;
    }
    if (opt.value === '__non_user__') {
      setNonUserView();
      return;
    }
    try {
      const userData = await getUser(opt.value);
      setViewAs(userData);
    } catch (err) {
      console.error('Failed to load user for View As:', err);
      clearViewAs();
      setSelectedOption(null);
    }
  };

  const handleClearViewAs = () => {
    clearViewAs();
    setSelectedOption(null);
    setNonUserInput('');
  };

  return (
    <>
      {isImpersonating && (
        <div style={{
          background: '#f1c21b', color: '#161616', textAlign: 'center',
          padding: '6px 16px', fontSize: '0.85rem', fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        }}>
          <span>👁 Viewing as: {user.name} ({user.alias}) — {user.tier}</span>
          <button onClick={handleClearViewAs} style={{
            background: '#161616', color: '#fff', border: 'none', borderRadius: 4,
            padding: '3px 10px', cursor: 'pointer', fontSize: '0.8rem',
          }}>Exit View As</button>
        </div>
      )}
      {viewAsNonUser && (
        <div style={{
          background: '#da1e28', color: '#fff', textAlign: 'center',
          padding: '6px 16px', fontSize: '0.85rem', fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        }}>
          <span>🚫 Viewing as: {nonUserAlias ? `${nonUserAlias} (non-user)` : 'Non-user (access denied preview)'}</span>
          <button onClick={handleClearViewAs} style={{
            background: '#fff', color: '#da1e28', border: 'none', borderRadius: 4,
            padding: '3px 10px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
          }}>Exit View As</button>
        </div>
      )}
      <TopNavigation
        identity={{
          title: 'Equipment Tracker',
          href: '/',
          logo: { src: '/logo.svg', alt: 'Equipment Tracker Logo' },
        }}
        utilities={[
          { type: 'button', text: 'AUP Policy', href: 'https://policy.a2z.com/docs/4741/publication', external: true },
          ...(user ? [
            {
              type: 'button',
              text: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <img
                    src={badgePhotoUrl}
                    alt={user.alias}
                    style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  {user.name} ({user.alias})
                </span>
              ),
              disabled: true,
            },
            { type: 'button', text: 'Sign Out', onClick: () => logout() },
          ] : []),
        ]}
      />
      {realUser?.alias === 'zaerio' && (
        <div style={{
          background: '#f4f4f4', borderBottom: '1px solid #e0e0e0',
          padding: '6px 16px', display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#525252', whiteSpace: 'nowrap' }}>
            👁 View as:
          </span>
          <div style={{ width: 280 }}>
            <Select
              selectedOption={selectedOption}
              onChange={handleViewAsChange}
              options={userOptions}
              placeholder="Select a user..."
              filteringType="auto"
              expandToViewport
            />
          </div>
          {(isImpersonating || viewAsNonUser) && (
            <button onClick={handleClearViewAs} style={{
              background: '#0f62fe', color: '#fff', border: 'none', borderRadius: 4,
              padding: '4px 12px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
            }}>Back to my view</button>
          )}
          <span style={{ fontSize: '0.82rem', color: '#8d8d8d', margin: '0 4px' }}>|</span>
          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#525252', whiteSpace: 'nowrap' }}>
            🚫 Non-user:
          </span>
          <input
            type="text"
            value={nonUserInput}
            onChange={(e) => setNonUserInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && nonUserInput.trim()) {
                setNonUserView(nonUserInput.trim());
                setSelectedOption({ value: '__non_user__', label: `🚫 ${nonUserInput.trim()}` });
              }
            }}
            placeholder="Type alias + Enter"
            style={{
              width: 140, padding: '3px 8px', fontSize: '0.82rem', border: '1px solid #c6c6c6',
              borderRadius: 4, outline: 'none',
            }}
          />
          <button
            onClick={() => {
              if (nonUserInput.trim()) {
                setNonUserView(nonUserInput.trim());
                setSelectedOption({ value: '__non_user__', label: `🚫 ${nonUserInput.trim()}` });
              } else {
                setNonUserView();
                setSelectedOption({ value: '__non_user__', label: '🚫 Non-user' });
              }
            }}
            style={{
              background: '#da1e28', color: '#fff', border: 'none', borderRadius: 4,
              padding: '3px 10px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
            }}
          >Preview</button>
        </div>
      )}
      <NavContent />
    </>
  );
}
